import { equals } from 'ramda';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import videojs from 'video.js';

import { playerManager } from '@src/controllers/PlayerManager';
import type { IPlayerProxy } from '@src/controllers/PlayerWrapper';
import PlayerError, { ErrorMessage } from '@src/errors/PlayerError';
import useSyncPlayerOptionsOnRender from '@src/react/hooks/useSyncPlayerOptionsOnRender';
import { Event } from '@src/types/event';
import type { UserPlayerOptions } from '@src/types/player';
import logger from '@src/utils/logger';

export type { IPlayerProxy };

export type Props = UserPlayerOptions & {
  id?: string;
  onReady?: videojs.ReadyCallback;
  placeholder?: JSX.Element;
};
export interface PlayerForwardedRef {
  getPlayerProxy(): IPlayerProxy | null | undefined;
  getPlayerProxyAsync(): Promise<IPlayerProxy | null | undefined>;
  getContainerElement(): HTMLDivElement | null;
}

type OptionsAndReadyCallback = Omit<Props, 'id'>;

const HK01WebVideoPlayer = forwardRef<PlayerForwardedRef, Props>(
  (props, ref) => {
    const { id, ...restOptions } = props;
    const { aspectRatio } = props;

    const optionsAndReadyCbRef = useRef<OptionsAndReadyCallback>(restOptions);
    optionsAndReadyCbRef.current = restOptions;

    const playerProxyRef = useRef<IPlayerProxy | null | undefined>(null);

    useSyncPlayerOptionsOnRender(restOptions, { playerProxyRef });

    const containerElementRef = useRef<HTMLDivElement>(null);

    const playerProxyCreationEventTargetRef = useRef<videojs.EventTarget>(
      new videojs.EventTarget()
    );

    const resolvePlayerPromiseRef = useRef(
      new Promise<void>((resolve) => {
        playerProxyCreationEventTargetRef.current.one(
          Event.X_PLAYER_CREATED,
          () => resolve()
        );
      })
    );

    useImperativeHandle<PlayerForwardedRef, PlayerForwardedRef>(
      ref,
      () => {
        return {
          getPlayerProxy: () => playerProxyRef.current,
          getPlayerProxyAsync: async () => {
            await resolvePlayerPromiseRef.current;
            return playerProxyRef.current;
          },
          getContainerElement: () => containerElementRef.current,
        };
      },
      []
    );

    useEffect(() => {
      if (!playerProxyRef.current) return;

      const originalAspectRatio = playerProxyRef.current.aspectRatio();
      const isAspectRatioChanged = originalAspectRatio !== aspectRatio;

      if (isAspectRatioChanged && aspectRatio) {
        playerProxyRef.current.aspectRatio(aspectRatio);
      }
    }, [aspectRatio]);

    useEffect(() => {
      (async () => {
        try {
          if (!containerElementRef.current) {
            throw new Error(
              ErrorMessage.FAIL_TO_FIND_CONTAINER_ELEMENT_TO_INITIATE_VIDEO_PLAYER
            );
          }
          const { onReady, ...restOptions } = optionsAndReadyCbRef.current;

          playerProxyRef.current = await playerManager?.createPlayerAsync(
            containerElementRef.current,
            restOptions,
            onReady
          );

          playerProxyCreationEventTargetRef.current.trigger(
            Event.X_PLAYER_CREATED
          );
        } catch (err: unknown) {
          const customErr = new PlayerError(
            ErrorMessage.FAIL_TO_CREATE_PLAYER,
            {
              cause: err,
            }
          );
          logger.error(customErr);
          optionsAndReadyCbRef.current.onError?.(customErr, undefined);
        }
      })();

      return () => {
        playerProxyRef.current?.dispose?.();
      };
    }, []);

    return (
      <div id={id} ref={containerElementRef}>
        {props.placeholder}
      </div>
    );
  }
);

export default memo(HK01WebVideoPlayer, (prevProps, nextProps) =>
  // R.equals will do a deep compare of props to prevent un-necessary re-renders
  equals(prevProps, nextProps)
) as typeof HK01WebVideoPlayer;
